import * as cascadeSelect from '@zag-js/cascade-select';
import { normalizeProps, useMachine } from 'zag-ripple';
import { track, trackSplit, Portal } from 'ripple';
import { ChevronDown, ChevronRight, Check, X } from 'lucide-ripple';

export component CascadeSelect() {
	let service = useMachine(cascadeSelect.machine, {
		id: 'cascade-select',
		collection,
	});
	let api = track(() => cascadeSelect.connect(service, normalizeProps));

	<div {...@api.getRootProps()}>
		<label {...@api.getLabelProps()}>{'Location'}</label>
		<div {...@api.getControlProps()}>
			<button {...@api.getTriggerProps()}>
				<span>{@api.valueAsString || 'Select location'}</span>
				<span {...@api.getIndicatorProps()}>
					<ChevronDown size={16} />
				</span>
			</button>
			<button {...@api.getClearTriggerProps()}>
				<X size={14} />
			</button>
		</div>
		<Portal target={document.body}>
			<div {...@api.getPositionerProps()}>
				<div {...@api.getContentProps()}>
					<TreeNode node={collection.rootNode} indexPath={[]} value={[]} {api} />
				</div>
			</div>
		</Portal>
	</div>
}

interface TreeNodeProps {
	node: Node;
	indexPath?: number[];
	value?: string[];
	api: cascadeSelect.Api;
}

component TreeNode(props) {
	let [indexPath, value, node, api] = trackSplit(props, ['indexPath', 'value', 'node', 'api']);
	let nodeProps = track(() => ({ indexPath: @indexPath, value: @value, item: @node }));
	let nodeState = track(() => @api.getItemState(@nodeProps));
	let children = track(() => collection.getNodeChildren(@node) || []);

	let highlightedChild = track(() => @nodeState.highlightedChild);
	let highlightedIndexPath = track(() => [...@indexPath, @nodeState.highlightedIndex]);
	let highlightedValue = track(
		() => @nodeState.highlightedChild
			? [...@value, collection.getNodeValue(@nodeState.highlightedChild)]
			: @value,
	);

	<ul {...@api.getListProps(@nodeProps)}>
		for (const item of @children; index index) {
			let itemProps = track(
				() => ({
					indexPath: [...@indexPath, index],
					value: [...@value, collection.getNodeValue(item)],
					item,
				}),
			);

			let itemState = track(() => @api.getItemState(@itemProps));

			<li {...@api.getItemProps(@itemProps)}>
				<span {...@api.getItemTextProps(@itemProps)}>{@item.label}</span>
				if (@itemState.hasChildren) {
					<span data-scope="cascade-select" data-part="branch-indicator">
						<ChevronRight size={14} />
					</span>
				}
				<span {...@api.getItemIndicatorProps(@itemProps)}>
					<Check size={12} />
				</span>
			</li>
		}
	</ul>
	if (@highlightedChild && collection.isBranchNode(@highlightedChild)) {
		<TreeNode
			node={@highlightedChild}
			indexPath={@highlightedIndexPath}
			value={@highlightedValue}
			{api}
		/>
	}
}

interface Node {
	label: string;
	value: string;
	children?: Node[];
}

const collection = cascadeSelect.collection<Node>(
	{
		nodeToValue: (node) => node.value,
		nodeToString: (node) => node.label,
		nodeToChildren: (node) => node.children ?? [],
		rootNode: {
			label: 'ROOT',
			value: 'ROOT',
			children: [
				{
					label: 'North America',
					value: 'north-america',
					children: [
						{
							label: 'United States',
							value: 'us',
							children: [
								{ label: 'New York', value: 'ny' },
								{ label: 'California', value: 'ca' },
								{ label: 'Texas', value: 'tx' },
							],
						},
						{
							label: 'Canada',
							value: 'ca-country',
							children: [
								{ label: 'Ontario', value: 'on' },
								{ label: 'British Columbia', value: 'bc' },
							],
						},
					],
				},
				{
					label: 'Africa',
					value: 'africa',
					children: [
						{
							label: 'Nigeria',
							value: 'ng',
							children: [
								{ label: 'Lagos', value: 'lagos' },
								{ label: 'Abuja', value: 'abuja' },
							],
						},
						{
							label: 'Kenya',
							value: 'ke',
							children: [
								{ label: 'Nairobi', value: 'nairobi' },
								{ label: 'Mombasa', value: 'mombasa' },
							],
						},
					],
				},
				{
					label: 'Asia',
					value: 'asia',
					children: [
						{
							label: 'Japan',
							value: 'jp',
							children: [
								{ label: 'Tokyo', value: 'tokyo' },
								{ label: 'Osaka', value: 'osaka' },
							],
						},
						{
							label: 'India',
							value: 'in',
							children: [
								{ label: 'Mumbai', value: 'mumbai' },
								{ label: 'Delhi', value: 'delhi' },
							],
						},
					],
				},
			],
		},
	},
);
